/**
 * Resolves multi-choice replies during conversational email browsing (V1.5).
 * Prefer resolveEmailSelection for single-email / memory turns before this.
 */

function normalizeAshleyConversationTranscript(transcript) {
  return String(transcript || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function matchesAny(normalized, phrases) {
  return phrases.some(
    (phrase) => normalized === phrase || normalized.startsWith(`${phrase} `),
  );
}

const OPEN_PHRASES = Object.freeze([
  "open my inbox",
  "open inbox",
  "open gmail",
  "open my gmail",
  "open my emails",
  "open emails",
  "launch gmail",
]);

const NEXT_PHRASES = Object.freeze([
  "next five",
  "the next five",
  "hear the next five",
  "hear next five",
  "review the next five",
  "review the next five unread emails",
  "review next five",
  "next unread emails",
  "next emails",
  "next ones",
  "next",
  "continue",
  "keep going",
  "more",
  "more emails",
  "show more",
  "read more",
]);

const REVIEW_ALL_PHRASES = Object.freeze([
  "review all unread",
  "review all unread emails",
  "list all unread emails",
  "list all unread",
  "show all unread",
  "show all unread emails",
  "who else emailed me",
  "who emailed me",
  "show me everyone",
  "read all senders",
  "list all senders",
  "show me all unread senders",
  "list every unread sender",
  "show everyone",
]);

const DONE_PHRASES = Object.freeze([
  "finish here",
  "finish",
  "all set",
  "im all set",
  "i am all set",
  "im good",
  "i am good",
  "im fine",
  "thats all",
  "that is all",
  "nothing else",
  "no thanks",
  "no thank you",
  "not now",
  "nope",
  "no",
  "cancel",
  "never mind",
  "nevermind",
  "stop",
]);

const SUMMARIZE_PHRASES = Object.freeze([
  "summarize",
  "summarize them",
  "summarize those",
  "summarize those emails",
  "summarize these",
  "summarize these emails",
  "summarize any",
  "summarize any of those",
  "summarize any of those emails",
  "summarize it",
  "summarize that email",
  "tell me about them",
  "tell me about those",
  "tell me about those emails",
  "read them",
  "read those",
  "read those emails",
  "yes",
  "yeah",
  "yep",
  "sure",
  "please",
  "ok",
  "okay",
  "go ahead",
]);

/**
 * @param {string} transcript
 * @param {{ allowSummarize?: boolean, allowNext?: boolean, allowReviewAll?: boolean }} [options]
 * @returns {"open" | "next" | "summarize" | "review_all" | "done" | null}
 */
export function resolveEmailBrowseChoice(transcript, options = {}) {
  const allowSummarize = options.allowSummarize !== false;
  const allowNext = options.allowNext !== false;
  const allowReviewAll = options.allowReviewAll === true;
  const normalized = normalizeAshleyConversationTranscript(transcript);
  if (!normalized) {
    return null;
  }

  // Open / next / review-all / done before summarize so "open my inbox" isn't treated as yes.
  if (matchesAny(normalized, OPEN_PHRASES)) {
    return "open";
  }
  if (allowReviewAll && matchesAny(normalized, REVIEW_ALL_PHRASES)) {
    return "review_all";
  }
  if (allowNext && matchesAny(normalized, NEXT_PHRASES)) {
    return "next";
  }
  if (matchesAny(normalized, DONE_PHRASES)) {
    return "done";
  }
  if (allowSummarize && matchesAny(normalized, SUMMARIZE_PHRASES)) {
    return "summarize";
  }

  return null;
}
